/**
 * A record made from a walk photo — the queue side of Make it a (Claude
 * Design "Review · walk photos", LED-1154).
 *
 * The Walk photos card (`WalkPhotosPane`) knows which of the walk's records
 * were made from one of its photos (`ConsoleWalkPhoto.convertedTo`, and the
 * reply of a Convert it just made). The record's own review card is drawn by
 * the queue, far from the pane, so the pane publishes that knowledge here and
 * the record card reads it with `useWalkPhotoOrigin(recordId)`:
 *
 *  - `WalkPhotoOriginTag` — "NEW OBSERVATION FROM PHOTO" above the kind pill;
 *  - `WalkPhotoOriginStrip` — "Made from a walk photo", the photo, and Undo,
 *    which puts the photo back on the Walk photos card and deletes the record
 *    (`undoWalkPhotoConvert`);
 *  - `fresh` — true for a moment after a Convert made here, while the card
 *    wears `WALK_PHOTO_FRESH_CLASS`: a green outline as it rises in.
 *
 * The Walk photos card's collapsed outcome (Sent / Not sent / Saved for
 * later) lives here too, so the queue can keep drawing the collapsed card
 * after the re-read that took its Photos cards out of the pending list.
 *
 * Module state, not React context: the pane and the record cards are
 * siblings deep inside `ReviewQueue`, and threading a provider through it
 * would touch every layer in between. It only bridges what this page did; a
 * reload rebuilds it from the server's `convertedTo`.
 */

import { useEffect, useRef, useState, useSyncExternalStore, type CSSProperties } from "react";
import { Check } from "lucide-react";
import { PhotoImage } from "../components/PhotoImage";
import { FONT_MONO, FONT_TEXT, WPC } from "../tokens";
import type { WalkPhotoConvertKind } from "../api";

export type WalkPhotoOrigin = {
  recordId: string;
  visitId: string;
  mediaId: string;
  kind: WalkPhotoConvertKind;
  /** The photo, as the Walk photos card shows it. */
  photoUrl: string | null;
  /** "1:08A". */
  photoTime: string;
  /** Set by a Convert made on this page; consumed by the first card that shows it. */
  freshPending?: boolean;
  /** Puts the photo back. Resolves to an error message, or null when done. */
  undo: () => Promise<string | null>;
};

export type WalkPhotosOutcome = {
  kind: "log" | "gallery" | "not-sent" | "later";
  /** The card's title count when it was decided. */
  count: number;
  /** The project's integration when it was decided: once its Photos cards
   *  are decided the queue no longer has a row to read it from. */
  integration?: "procore" | "autodesk" | "local";
};

const origins = new Map<string, WalkPhotoOrigin>();
const outcomes = new Map<string, WalkPhotosOutcome>();
const listeners = new Set<() => void>();
let version = 0;

function emit() {
  version += 1;
  for (const listener of listeners) listener();
}
function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
const snapshot = () => version;

/** Replace what one walk's Walk photos card knows about its records. A
 *  pending `freshPending` survives a re-publish from a read. */
export function publishWalkPhotoOrigins(visitId: string, next: WalkPhotoOrigin[]): void {
  let changed = false;
  for (const [id, origin] of origins) {
    // A Convert made here a moment ago outlives a read that predates it; its
    // card takes the flag when it shows, and the next read then has it.
    if (origin.visitId === visitId && !origin.freshPending && !next.some((o) => o.recordId === id)) {
      origins.delete(id);
      changed = true;
    }
  }
  for (const origin of next) {
    const current = origins.get(origin.recordId);
    if (current && current.mediaId === origin.mediaId && current.kind === origin.kind && current.photoUrl === origin.photoUrl) {
      // Same facts; keep the fresh flag and refresh the undo closure quietly.
      current.undo = origin.undo;
      continue;
    }
    origins.set(origin.recordId, { ...origin, freshPending: origin.freshPending || current?.freshPending });
    changed = true;
  }
  if (changed) emit();
}

export function forgetWalkPhotoOrigin(recordId: string): void {
  if (origins.delete(recordId)) emit();
}

export function setWalkPhotosOutcome(visitId: string, outcome: WalkPhotosOutcome | null): void {
  if (outcome) outcomes.set(visitId, outcome);
  else if (!outcomes.delete(visitId)) return;
  emit();
}

export function walkPhotosOutcome(visitId: string): WalkPhotosOutcome | undefined {
  return outcomes.get(visitId);
}

/** For the queue: whether a walk has a decided Walk photos card to keep drawing. */
export function useWalkPhotosOutcome(visitId: string | null | undefined): WalkPhotosOutcome | undefined {
  useSyncExternalStore(subscribe, snapshot, snapshot);
  return visitId ? outcomes.get(visitId) : undefined;
}

/** Tests only. */
export function resetWalkPhotoOrigins(): void {
  origins.clear();
  outcomes.clear();
  emit();
}

/** How long a just-made record wears its green outline (the design's 2.6s). */
export const FRESH_MS = 2600;
export const WALK_PHOTO_FRESH_CLASS = "wpo-fresh";

/**
 * The origin of the record `recordId`, if it was made from a walk photo, and
 * whether it was made here a moment ago. The first card to show a fresh
 * origin takes the flag, so a re-render or a remount does not replay it.
 */
export function useWalkPhotoOrigin(recordId: string): (WalkPhotoOrigin & { fresh: boolean }) | undefined {
  useSyncExternalStore(subscribe, snapshot, snapshot);
  const origin = origins.get(recordId);
  const [freshUntil, setFreshUntil] = useState(0);
  useEffect(() => {
    if (!origin?.freshPending) return;
    origin.freshPending = false;
    setFreshUntil(Date.now() + FRESH_MS);
  }, [origin]);
  useEffect(() => {
    if (!freshUntil) return;
    const timer = window.setTimeout(() => setFreshUntil(0), Math.max(0, freshUntil - Date.now()));
    return () => window.clearTimeout(timer);
  }, [freshUntil]);
  return origin ? { ...origin, fresh: freshUntil > 0 } : undefined;
}

/** Drop the origin's own photo from a record card's media list: the strip
 *  shows it, with the words that say where it came from. */
export function withoutOriginPhoto<T extends { id: string }>(photos: T[], origin: { mediaId: string } | undefined): T[] {
  return origin ? photos.filter((photo) => photo.id !== origin.mediaId) : photos;
}

export function originKindLabel(kind: WalkPhotoConvertKind): string {
  return kind === "observation" ? "OBSERVATION" : kind === "punch" ? "PUNCH ITEM" : kind === "task" ? "TASK" : "DAILY LOG";
}

const mono: CSSProperties = { fontFamily: FONT_MONO, fontWeight: 700, textTransform: "uppercase" };

/** The outline and the rise, for a card that just appeared. `!important`
 *  because the record card sets its shadow inline. */
export function WalkPhotoFreshStyle() {
  return (
    <style>{`@keyframes wpoRise{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:none}}
.${WALK_PHOTO_FRESH_CLASS}{box-shadow:0 0 0 2px ${WPC.green},0 10px 28px rgba(20,17,12,.08)!important;animation:wpoRise .4s cubic-bezier(.2,.8,.2,1) both!important;transition:box-shadow .3s}
@media (prefers-reduced-motion: reduce){.${WALK_PHOTO_FRESH_CLASS}{animation:none!important}}`}</style>
  );
}

/** "✓ NEW OBSERVATION FROM PHOTO" — above the record card's kind pill. */
export function WalkPhotoOriginTag({ origin }: { origin: WalkPhotoOrigin & { fresh: boolean } }) {
  const ref = useRef<HTMLDivElement>(null);
  // A record that just appeared above the Walk photos card is brought into
  // view, gently — the reader's eye is on the photo they just converted.
  useEffect(() => {
    if (origin.fresh) ref.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [origin.fresh]);
  return (
    <>
      <WalkPhotoFreshStyle />
      <div
        ref={ref}
        data-testid="walk-photo-origin-tag"
        style={{ ...mono, display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 10, fontSize: 8.5, letterSpacing: "1.2px", color: WPC.greenInk, background: WPC.greenSoft, padding: "3px 8px", borderRadius: 5 }}
      >
        <Check size={9} color={WPC.green} strokeWidth={3} aria-hidden />
        {`NEW ${originKindLabel(origin.kind)} FROM PHOTO`}
      </div>
    </>
  );
}

/** "Made from a walk photo" — the photo, and Undo. */
export function WalkPhotoOriginStrip({ origin, disabled }: { origin: WalkPhotoOrigin; disabled?: boolean }) {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const undo = async () => {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    const error = await origin.undo();
    setBusy(false);
    if (error) setProblem(error);
  };
  return (
    <div data-testid="walk-photo-origin" style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12, fontFamily: FONT_TEXT }}>
      <span style={{ position: "relative", flex: "none", width: 96, height: 96, borderRadius: 10, overflow: "hidden", background: WPC.tile, display: "block" }}>
        {origin.photoUrl ? (
          <PhotoImage src={origin.photoUrl} alt="" loading="lazy" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        ) : null}
        <span style={{ ...mono, position: "absolute", left: 6, bottom: 6, fontSize: 8, color: WPC.white, background: "rgba(10,10,10,.6)", padding: "2px 5px", borderRadius: 4 }}>
          {origin.photoTime}
        </span>
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ ...mono, fontSize: 8.5, letterSpacing: "1.2px", color: WPC.label }}>Made from a walk photo</div>
        <div style={{ fontSize: 12.5, lineHeight: 1.45, color: WPC.sub, marginTop: 4 }}>
          The photo moved off the walk-photos card and is attached here.{" "}
          <button
            type="button"
            data-testid="walk-photo-origin-undo"
            disabled={disabled || busy}
            onClick={() => void undo()}
            style={{ border: "none", background: "none", padding: 0, font: "inherit", fontWeight: 700, color: WPC.ink, textDecoration: "underline", textUnderlineOffset: 3, cursor: disabled ? "default" : "pointer" }}
          >
            {busy ? "Putting it back…" : "Undo"}
          </button>{" "}
          puts it back.
        </div>
        {problem ? <div role="alert" style={{ fontSize: 12.5, color: WPC.redInk, marginTop: 4 }}>{problem}</div> : null}
      </div>
    </div>
  );
}
